import { GeneralCatalogItem } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { DisplayItem } from 'design-system';
import { useCallback, useMemo } from 'react';
import { AnyAction } from 'redux';
// functions
import {
  findIdByCode,
  mapCatatalogToDisplay as mapToDisplay,
} from 'src/functions/mapCatatalogToDisplay';
// store
import {
  CITIES_REQUEST,
  COUNTRIES_REQUEST,
  STATES_REQUEST,
  TOWNSHIPS_REQUEST,
} from 'src/store/legalEntityCatalog/actions/legalEntityRequestIdentifiers';

type Props = {
  countries: GeneralCatalogItem[];
  cities: GeneralCatalogItem[];
  countryStates: GeneralCatalogItem[];
  townships: GeneralCatalogItem[];
  getCitiesAction: (value: string | number) => Promise<AnyAction>;
  getTownshipsAction: (value: string | number) => Promise<AnyAction>;
};

type HookResult = {
  requestingCountries: boolean;
  requestingStates: boolean;
  requestingCities: boolean;
  requestingTownships: boolean;
  countryOptions: DisplayItem[];
  stateOptions: DisplayItem[];
  cityOptions: DisplayItem[];
  townshipOptions: DisplayItem[];
  fetchCities: (value: string) => void;
  fetchTownships: (value: string) => void;
};

/**
 * Returns external parameters provided by the old link
 */
export default function useAddressInformation({
  countries,
  cities,
  countryStates,
  townships,
  getCitiesAction,
  getTownshipsAction,
}: Props): HookResult {
  const countriesRequestStatus = useNetworkObserver(COUNTRIES_REQUEST);
  const statesRequestStatus = useNetworkObserver(STATES_REQUEST);
  const citiesRequestStatus = useNetworkObserver(CITIES_REQUEST);
  const townshipsRequestStatus = useNetworkObserver(TOWNSHIPS_REQUEST);

  const countryOptions = useMemo(() => mapToDisplay(countries), [countries]);
  const stateOptions = useMemo(
    () => mapToDisplay(countryStates),
    [countryStates],
  );
  const cityOptions = useMemo(() => mapToDisplay(cities), [cities]);
  const townshipOptions = useMemo(() => mapToDisplay(townships), [townships]);

  const fetchCities = useCallback(
    (newState: string): void => {
      getCitiesAction(findIdByCode(countryStates, newState));
    },
    [countryStates, getCitiesAction],
  );

  const fetchTownships = useCallback(
    (newCity: string): void => {
      getTownshipsAction(findIdByCode(cities, newCity));
    },
    [cities, getTownshipsAction],
  );

  return {
    requestingCountries: countriesRequestStatus === 'requesting',
    requestingStates: statesRequestStatus === 'requesting',
    requestingCities: citiesRequestStatus === 'requesting',
    requestingTownships: townshipsRequestStatus === 'requesting',
    countryOptions,
    stateOptions,
    cityOptions,
    townshipOptions,
    fetchCities,
    fetchTownships,
  };
}
